import GoogleIcon from '@mui/icons-material/Google';
import { Alert, Box, Button, Container, Divider, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import SignupForm, { SignupFormRef, signupSchema } from './components/SignupForm';
import { AvanzuHeader } from '../../components/AvanzuHeader';

type SignupFormData = yup.InferType<typeof signupSchema>;

const REFERRAL_CODE_KEY = 'referralCode';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const signupFormRef = useRef<SignupFormRef>(null);

  // Persist the affiliate referral code so it survives navigating away and back
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem(REFERRAL_CODE_KEY, ref);
    }
  }, [searchParams]);

  const getReferralCode = (): string | null =>
    searchParams.get('ref') || localStorage.getItem(REFERRAL_CODE_KEY);

  const oauthRefSuffix = () => {
    const ref = getReferralCode();
    return ref ? `&ref=${encodeURIComponent(ref)}` : '';
  };

  const handleGoogleSignup = () => {
    setError(null);
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/oauth/google?action=signup${oauthRefSuffix()}`;
  };

  const handleXeroSignup = () => {
    setError(null);
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/oauth/xero?action=signup${oauthRefSuffix()}`;
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, referralCode: getReferralCode() || undefined }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle API errors - the backend returns { success: false, message: "error message" }
        throw new Error(result.message || 'Signup failed');
      }

      // Success case
      const returnUrl = `/verify-email-pending?email=${encodeURIComponent(data.email)}`;
      navigate(returnUrl);
    } catch (error) {
      console.error('Signup error:', error);
      signupFormRef.current?.reset();
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <AvanzuHeader />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" align="center" gutterBottom>
            Create new Avanzu account
          </Typography>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Start managing your R&D activities today
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignup}
              disabled={isLoading}
              sx={{
                bgcolor: '#4285F4',
                '&:hover': { bgcolor: '#357ae8' },
              }}
            >
              Sign up with Google
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={handleXeroSignup}
              disabled={isLoading}
              sx={{
                bgcolor: '#13B5EA',
                '&:hover': { bgcolor: '#0e8bb5' },
              }}
            >
              Sign up with Xero
            </Button>
          </Stack>

          <Divider sx={{ my: 3, color: 'text.secondary' }}>OR</Divider>

          <SignupForm
            onSubmit={handleSignupSubmit}
            isLoading={isLoading}
            submitButtonText={'Create account'}
          />
        </Paper>
      </Box>
    </Container>
  );
};

export default Signup;
